/**
 * Slash commands and message components: each server's command list (fetched when first
 * needed), the state of commands you've run that haven't produced a message yet (thinking,
 * failed, or an ephemeral reply only you can see; nothing here is stored on the server),
 * and the buttons/select menus you've used that the bot hasn't answered yet.
 */
import { create } from 'zustand';
import type { ApplicationCommand, GatewayEvents, RichEmbed } from '@comms/shared';
import { api } from '../lib/api';

export interface InteractionNotice {
  id: string;
  channelId: string;
  /** what was used: a command's name ("/ping"), or a button or menu's label */
  label: string;
  applicationId: string;
  state: 'pending' | 'thinking' | 'failed' | 'ephemeral';
  content?: string;
  embeds?: RichEmbed[];
}

interface CommandsState {
  byGuild: Record<string, ApplicationCommand[] | undefined>;
  /** channelId -> notices, oldest first */
  notices: Record<string, InteractionNotice[]>;
  /** messageId -> the customId of the component waiting for the bot */
  usingComponent: Record<string, string>;
}

export const useCommands = create<CommandsState>(() => ({
  byGuild: {},
  notices: {},
  usingComponent: {},
}));
const set = useCommands.setState;

/** If the server's own timeout never reaches us (e.g. it restarted), give up after this. */
const CLIENT_TIMEOUT_MS = 8000;

const loading = new Set<string>();
export function loadCommands(guildId: string) {
  if (useCommands.getState().byGuild[guildId] || loading.has(guildId)) return;
  loading.add(guildId);
  api<ApplicationCommand[]>('GET', `/guilds/${guildId}/commands`)
    .then(
      (list) => set((s) => ({ byGuild: { ...s.byGuild, [guildId]: list } })),
      () => {},
    )
    .finally(() => loading.delete(guildId));
}

export function invalidateCommands(guildId: string) {
  set((s) => {
    if (!(guildId in s.byGuild)) return {};
    const byGuild = { ...s.byGuild };
    delete byGuild[guildId];
    return { byGuild };
  });
}

/** Interactions that finished before the POST that started them returned. */
const finished = new Set<string>();

function patchNotice(channelId: string, id: string, patch: Partial<InteractionNotice> | null) {
  set((s) => {
    const list = s.notices[channelId] ?? [];
    const next =
      patch === null
        ? list.filter((n) => n.id !== id)
        : list.some((n) => n.id === id)
          ? list.map((n) => (n.id === id ? { ...n, ...patch } : n))
          : [...list, { id, channelId, ...patch } as InteractionNotice];
    return { notices: { ...s.notices, [channelId]: next } };
  });
}

export async function runCommand(
  channelId: string,
  cmd: ApplicationCommand,
  options: Record<string, string | number | boolean>,
) {
  const { id } = await api<{ id: string }>('POST', `/channels/${channelId}/interactions`, {
    commandId: cmd.id,
    options,
  });
  if (finished.has(id)) return;
  const current = useCommands.getState().notices[channelId]?.find((n) => n.id === id);
  if (!current)
    patchNotice(channelId, id, {
      label: `/${cmd.name}`,
      applicationId: cmd.applicationId,
      state: 'pending',
    });
  window.setTimeout(() => {
    const n = useCommands.getState().notices[channelId]?.find((x) => x.id === id);
    if (n?.state === 'pending') patchNotice(channelId, id, { state: 'failed' });
  }, CLIENT_TIMEOUT_MS);
}

/** Component interactions waiting for the bot: id -> where it was used. */
const componentUses = new Map<
  string,
  { channelId: string; messageId: string; label: string; applicationId: string }
>();

function stopUsing(messageId: string) {
  set((s) => {
    const usingComponent = { ...s.usingComponent };
    delete usingComponent[messageId];
    return { usingComponent };
  });
}

/** Click a button or pick from a select menu on a bot's message. */
export async function sendComponent(
  channelId: string,
  message: { id: string; authorId: string },
  customId: string,
  label: string,
  values?: string[],
) {
  set((s) => ({ usingComponent: { ...s.usingComponent, [message.id]: customId } }));
  let id: string;
  try {
    ({ id } = await api<{ id: string }>(
      'POST',
      `/channels/${channelId}/messages/${message.id}/interactions`,
      { customId, ...(values ? { values } : {}) },
    ));
  } catch (err) {
    stopUsing(message.id);
    throw err;
  }
  if (finished.has(id)) return stopUsing(message.id);
  componentUses.set(id, {
    channelId,
    messageId: message.id,
    label,
    applicationId: message.authorId,
  });
  window.setTimeout(() => {
    if (!componentUses.delete(id)) return;
    stopUsing(message.id);
    patchNotice(channelId, id, { label, applicationId: message.authorId, state: 'failed' });
  }, CLIENT_TIMEOUT_MS);
}

/** A bot edited the message: whatever was being used on it has been answered. */
export function onMessageUpdated(messageId: string) {
  if (useCommands.getState().usingComponent[messageId]) stopUsing(messageId);
}

export function onInteractionUpdate(d: GatewayEvents['INTERACTION_UPDATE']) {
  const use = componentUses.get(d.interactionId);
  if (d.messageId) {
    componentUses.delete(d.interactionId);
    stopUsing(d.messageId);
  }
  if (d.state === 'done' || d.state === 'deferred') {
    finished.add(d.interactionId);
    patchNotice(d.channelId, d.interactionId, null);
    return;
  }
  if (d.state === 'ephemeral' || d.state === 'failed') finished.add(d.interactionId);
  patchNotice(d.channelId, d.interactionId, {
    label: d.commandName ? `/${d.commandName}` : (use?.label ?? 'a component'),
    applicationId: d.application.id,
    state: d.state,
    ...(d.content !== undefined ? { content: d.content } : {}),
    ...(d.embeds ? { embeds: d.embeds } : {}),
  });
}

export function dismissNotice(channelId: string, id: string) {
  patchNotice(channelId, id, null);
}

export const resetCommands = () => set({ byGuild: {}, notices: {}, usingComponent: {} }, true);
